/**
 * The order aggregate.
 *
 * Specification: ../test/order.spec.ts and ../test/convergence.spec.ts.
 * Master plan §2 D5/D6/D8/D9/D13, §4 M3, §9 T2.
 *
 * A PURE fold over an append-only event stream. No database, no clock, no
 * network — because the identical reduction has to run on a handheld that has
 * been offline for four hours and on the server that later ingests its outbox,
 * and reach byte-identical state. Anything impure here becomes a divergence
 * between what the guest was shown and what the books say.
 *
 * Five rules the shape enforces rather than documents:
 *
 *   - Events are union-mergeable FACTS, never assignments. `SelectionAdded`,
 *     `SelectionVoided` — never "set quantity = 3". Two handhelds on one check
 *     is ordinary in table service, and last-writer-wins on a monetary field
 *     silently discards one of them.
 *   - A SENT selection is immutable in quantity and price. Changing one is
 *     Void plus a new Selection, and `firedAt` survives the void because
 *     fired-but-not-billed is the strongest theft signal there is (D13).
 *   - Splitting is ALLOCATION over an immutable selection set. Checks hold
 *     weights; selections are never copied. Un-splitting is free and
 *     "split then add an item" simply works.
 *   - Order-sensitive fields are derived from a stamped LOG, not from arrival
 *     order. Fulfillment is the only genuinely contended field, and it is
 *     resolved by HLC so two replicas that saw the same events in different
 *     orders agree. See `deriveFulfillment`.
 *   - Anomalies are flagged, never thrown. A reducer that throws on a
 *     surprising event wedges an offline device's outbox forever, and an
 *     evening's takings sit on a tablet nobody can drain.
 */

// The money subpath, not the barrel. The order aggregate runs in the POS
// bundle, and @gf/kernel's index pulls in tenancy (node:async_hooks), jobs and
// numbering (postgres) — none of which can exist in a browser.
import { allocateProportionally, vatFromGross, type RateBp } from "@gf/kernel/money";
import type { AllergenCode, LineModifier, TenderKind } from "@gf/contracts/values";

// --- Value types -----------------------------------------------------------

export type OrderType =
  | "DINE_IN"
  | "TAKEAWAY"
  /** Paid at the counter, collected by a number: a café's ordinary sale. */
  | "COUNTER"
  | "DELIVERY"
  | "BAR_TAB"
  | "KIOSK"
  | "AGGREGATOR"
  | "QR"
  | "EVENT"
  | "ROOM";

export type ConsumptionMode = "DINE_IN" | "TAKEAWAY" | "DELIVERY";

/**
 * Monotonic except via an explicit Recall. The rank is what makes "monotonic"
 * checkable rather than aspirational.
 */
export type FulfillmentState =
  | "NEW"
  | "HELD"
  | "SENT"
  | "IN_PROGRESS"
  | "READY"
  | "DELIVERED";

const FULFILLMENT_RANK: Readonly<Record<FulfillmentState, number>> = {
  NEW: 0,
  HELD: 1,
  SENT: 2,
  IN_PROGRESS: 3,
  READY: 4,
  DELIVERED: 5,
};

const BY_RANK = (Object.keys(FULFILLMENT_RANK) as FulfillmentState[]).sort(
  (a, b) => FULFILLMENT_RANK[a] - FULFILLMENT_RANK[b],
);

/**
 * Re-exported from the contract rather than declared here.
 *
 * It moved the day `accounting` needed to split a day's takings by payment form
 * and could not import this module to learn what the forms are — the same
 * journey `AllergenCode` made, for the same reason. Kept as a re-export so
 * every existing importer is undisturbed.
 */
export type { TenderKind } from "@gf/contracts/values";

/**
 * The two allowance kinds that reduce what a guest pays.
 *
 * They are NOT interchangeable. A DISCOUNT is a price reduction: it reduces
 * the taxable base. A COMP is "the guest received it and we are not charging":
 * no consideration, so no output VAT, the cost lands in a comp expense account,
 * and the inventory was still consumed. Booking a comp as a 100 % discount
 * destroys food-cost and loss-prevention analytics permanently — the two
 * cannot be told apart after the fact.
 */
export type AllowanceKind = "DISCOUNT" | "COMP";

/** The price and rate snapshotted onto a line at ring time. Never recomputed. */
export interface PricedLine {
  readonly grossMinor: bigint;
  readonly vatMinor: bigint;
  readonly netMinor: bigint;
  readonly rateBp: RateBp;
  /** Citation, so a receipt can be re-explained after the rules have moved. */
  readonly taxSourceRef: string;
}

export interface Allocation {
  readonly checkId: string;
  /** Basis points of the selection. All allocations must sum to 10 000. */
  readonly weightBp: number;
}

export interface VoidRecord {
  readonly at: Date;
  readonly reason: string;
  readonly authorizedBy: string;
}

/**
 * One stamped report about how far a line has got.
 *
 * Kept as a log rather than collapsed into a field so the result does not
 * depend on arrival order: a KDS whose outbox drained late must not be able to
 * push a line backwards just because its message landed second.
 */
export interface FulfillmentObservation {
  readonly kind: "PROGRESS" | "RECALL";
  readonly rank: number;
  readonly hlc: string;
  readonly eventId: string;
}

export interface SelectionState {
  readonly selectionId: string;
  readonly itemRef: string;
  readonly name: string;
  readonly quantityMilli: number;
  readonly priced: PricedLine;
  /** Annex II codes, as the menu declared them when this was rung. */
  readonly allergens: readonly AllergenCode[];
  /** Derived from `fulfillmentLog`; never assigned directly. */
  readonly fulfillmentState: FulfillmentState;
  readonly fulfillmentLog: readonly FulfillmentObservation[];
  /**
   * Server-persisted, the EARLIEST fire seen, and NOT cleared by a void or a
   * recall.
   *
   * An item that reached the kitchen and then left the check is food that
   * walked out unbilled. Most POS cannot compute this because they overwrite
   * the check rather than appending to it.
   */
  readonly firedAt: Date | null;
  readonly allocations: readonly Allocation[];
  readonly voided: VoidRecord | null;
  /**
   * FROZEN AT RING TIME, exactly like the price and the allergens.
   *
   * Whether this line needs somebody to have looked at a face. Not looked up
   * at fire time, for the same reason the allergens are not: the menu can move
   * between ringing and firing, and a lookup that misses would silently turn a
   * gated line into an ungated one — the direction that ends in a fine.
   */
  readonly ageRestricted: boolean;
  /**
   * Who confirmed they checked, or null.
   *
   * THE WHOLE POINT OF THE CONTROL. §11(5) of zákon 65/2017 Sb. carries an
   * exposure of up to 1 000 000 CZK, and what a venue needs when somebody asks
   * is not "our system prompts staff" — it is a name, a line and a timestamp.
   * A prompt that were not recorded would be a user-interface decoration.
   */
  readonly ageVerifiedBy: string | null;
  readonly ageVerifiedAt: Date | null;
  readonly seatNumber?: number;
  readonly courseId?: string;
  /** As chosen when rung, in the menu's words. Their price is already in `priced`. */
  readonly modifiers: readonly LineModifier[];
  /**
   * Who rang it — a member of staff, or "guest" for a phone at the table. Decides
   * whether the age check is already done (a server looked at the guest) or still
   * owed (nobody did).
   */
  readonly addedBy: string;
  /** Free text for the kitchen, or null. */
  readonly note: string | null;
}

export interface AllowanceState {
  readonly allowanceId: string;
  readonly checkId: string;
  readonly kind: AllowanceKind;
  readonly selectionId: string;
  readonly amountMinor: bigint;
  readonly reasonCode: string;
  readonly authorizedBy: string;
}

export interface TenderState {
  readonly tenderId: string;
  readonly checkId: string;
  readonly kind: TenderKind;
  /** What settles the check. */
  readonly amountMinor: bigint;
  /** On top of the check. A liability to staff, never revenue. */
  readonly tipMinor: bigint;
  /**
   * The cash-rounding line. Non-taxable and outside the VAT base.
   *
   * SIGN CONVENTION, stated once and used everywhere: POSITIVE when the venue
   * keeps the difference. A 293,50 check settled with 294,00 in cash carries
   * `amountMinor: 29400, roundingMinor: 50`, and the identity is
   *
   *     Σ amountMinor == dueMinor + Σ roundingMinor
   *
   * The same sign feeds the drawer expectation in @gf/module-cash, where the
   * 50 haléře are physically present. Flipping it between the two is a
   * per-transaction drift in one direction, which is indistinguishable from a
   * cashier skimming change.
   */
  readonly roundingMinor: bigint;
  /**
   * Reversed, and never deleted.
   *
   * A guest changes their mind between "cash" and "card" constantly, and the
   * CASH ROUNDING MUST GO WITH THE TENDER when they do — it is a function of
   * the tender, not of the check (M2). Voiding the tender therefore removes
   * both its amount and its rounding from the balance in one step, which is
   * what makes the reversal impossible to get half-right.
   *
   * The row stays, because a tender rung and reversed at 23:40 on a check that
   * then walked is exactly the pattern M8's fraud rules look for.
   */
  readonly voided: TenderVoid | null;
}

export interface TenderVoid {
  readonly reasonCode: string;
  readonly authorizedBy: string;
}

/** Who a full tax document is made out to. §29 ZDPH. */
export interface CheckBuyer {
  readonly legalName: string;
  readonly registrationId: string | null;
  readonly vatId: string | null;
  readonly address: string | null;
  readonly source: "ARES" | "MANUAL";
}

export interface CheckState {
  readonly checkId: string;
  readonly closedAt: Date | null;
  /**
   * Null until somebody asks for a full document.
   *
   * On the CHECK rather than the order, because a split table is one order and
   * several documents — and the one guest who needs a faktura for their
   * employer must not put their company's name on everybody else's receipt.
   */
  readonly buyer: CheckBuyer | null;
  /** Taken back after it closed: a corrective document, never an edit. */
  readonly refund: CheckRefund | null;
}

export interface CheckRefund {
  readonly refundId: string;
  readonly at: Date;
  readonly reasonCode: string;
  readonly authorizedBy: string;
  /** Mirrors what was paid, kind by kind. Positive amounts, given back. */
  readonly tenders: readonly { readonly kind: TenderKind; readonly amountMinor: bigint }[];
  /** The corrective document's own number, from the same gapless series. */
  readonly receiptNumber: string;
}

export type AnomalyKind =
  | "ALREADY_OPEN"
  | "ORDER_NOT_OPEN"
  | "UNKNOWN_SELECTION"
  | "UNKNOWN_CHECK"
  | "DUPLICATE_SELECTION"
  | "SENT_SELECTION_IMMUTABLE"
  | "NON_MONOTONIC_FULFILLMENT"
  | "ALLOCATION_NOT_WHOLE"
  | "ALLOWANCE_WITHOUT_REASON"
  | "ALLOWANCE_EXCEEDS_LINE"
  | "CHECK_NOT_BALANCED"
  | "VOIDED_SELECTION"
  | "UNKNOWN_TENDER"
  | "TENDER_VOID_WITHOUT_REASON"
  | "BAD_QUANTITY"
  | "HAS_ALLOWANCE"
  | "ORDER_NOT_EMPTY"
  | "ORDER_WAS_FIRED"
  | "ORDER_HAS_TENDERS"
  | "CHECK_NOT_CLOSED"
  | "REFUND_WITHOUT_REASON"
  | "REFUND_NOT_WHOLE"
  | "ALREADY_REFUNDED";

/**
 * A rejected event, kept on the state rather than thrown.
 *
 * This is an operator-facing queue (D11): each one is something a device
 * believed happened and the aggregate would not accept.
 *
 * Note that anomalies are observations about a STREAM, not part of the
 * aggregate's value: two replicas that saw the same events in different orders
 * agree on every selection, check and total, but may legitimately have noticed
 * different things on the way there.
 */
export interface Anomaly {
  readonly kind: AnomalyKind;
  readonly eventId: string;
  readonly detail: string;
}

// --- Events ----------------------------------------------------------------

interface Envelope {
  /** Client-generated UUIDv7. The global idempotency key. */
  readonly eventId: string;
  readonly occurredAt: Date;
  /**
   * Hybrid logical clock, formatted so a lexicographic compare agrees with
   * compareHlc. It orders contended fields; it never decides a monetary
   * outcome.
   */
  readonly hlc: string;
  readonly actorId: string;
}

export type OrderEvent =
  | (Envelope & {
      type: "order.opened";
      payload: {
        orderId: string;
        orderType: OrderType;
        consumptionMode: ConsumptionMode;
        /** The order opens with one check; splitting adds more. */
        checkId: string;
        tableRef?: string;
        /** The number a counter guest is called by ("A07"); absent at a table. */
        pickupCode?: string;
        /** Guests at the table, when the till asked. */
        covers?: number;
        /** The booking the table was seated from. */
        reservationRef?: string;
      };
    })
  | (Envelope & {
      type: "order.consumption_mode_changed";
      payload: { consumptionMode: ConsumptionMode; reason: string };
    })
  /** The party moved: table 4 to the bar. Everything else about the order stays. */
  | (Envelope & { type: "order.table_changed"; payload: { tableRef: string } })
  /** Opened by mistake and never used. Frees the table; no document, no number. */
  | (Envelope & { type: "order.abandoned"; payload: { reasonCode: string } })
  | (Envelope & { type: "check.opened"; payload: { checkId: string } })
  | (Envelope & { type: "check.closed"; payload: { checkId: string } })
  /**
   * Who the document is made out to. §29 ZDPH, above the §30a limit.
   *
   * On the CHECK rather than the order: a split table is one order and several
   * documents, and the one guest who needs a faktura for their employer must
   * not put their company's name on everybody else's receipt.
   */
  | (Envelope & {
      type: "check.buyer_identified";
      payload: {
        checkId: string;
        legalName: string;
        registrationId?: string;
        vatId?: string;
        address?: string;
        source: string;
      };
    })
  /**
   * The gapless receipt number the till issued for a check, immediately before
   * closing it.
   *
   * ON THIS UNION BUT NOT IN `ORDER_TYPES`: it rides the Order stream so the
   * fiscal projection can fold the number and the close in one ordered
   * sequence, and the aggregate IGNORES it, because a receipt number changes
   * nothing about what was sold. Exactly the position `ticket.fired` is in.
   */
  | (Envelope & {
      type: "receipt.issued";
      payload: {
        checkId: string;
        leaseId: string;
        seriesKey: string;
        period: string;
        number: number;
        receiptNumber: string;
      };
    })
  | (Envelope & {
      type: "selection.added";
      payload: {
        selectionId: string;
        itemRef: string;
        name: string;
        quantityMilli: number;
        priced: PricedLine;
        /**
         * FROZEN AT RING TIME, exactly like the price.
         *
         * The kitchen ticket needs them at FIRE time, and looking them up then
         * means a lookup that can fail — after a menu change, after a reload —
         * and the only thing to send when it does is an empty list. An empty
         * list is a positive claim that nothing is declared, which is the one
         * collapse the whole four-valued model exists to prevent.
         */
        allergens?: readonly AllergenCode[];
        /**
         * Frozen with them. Optional on the way in because streams written
         * before this field existed have none, and `false` is the only reading
         * available for those — see the reducer for why that is the
         * uncomfortable-but-correct default.
         */
        ageRestricted?: boolean;
        /** Defaults to the check the order opened with. */
        checkId?: string;
        seatNumber?: number;
        courseId?: string;
        /** Absent on lines rung before modifiers existed, which had none. */
        modifiers?: readonly LineModifier[];
        note?: string;
      };
    })
  | (Envelope & {
      type: "selection.repriced";
      payload: { selectionId: string; priced: PricedLine; reason: string };
    })
  /** Five beers are one line of five. Only before it is sent; the price moves with it. */
  | (Envelope & {
      type: "selection.requantified";
      payload: { selectionId: string; quantityMilli: number; priced: PricedLine };
    })
  | (Envelope & {
      /** Re-resolved VAT. The gross does not move; the net/VAT split does. */
      type: "selection.retaxed";
      payload: {
        selectionId: string;
        rateBp: RateBp;
        vatMinor: bigint;
        netMinor: bigint;
        taxSourceRef: string;
      };
    })
  | (Envelope & {
      type: "selection.sent";
      payload: { selectionIds: readonly string[]; stationRef: string };
    })
  | (Envelope & {
      /**
       * A member of staff confirms they checked somebody's age.
       *
       * ITS OWN EVENT rather than a flag on the fire, because the two are
       * different acts by potentially different people at different times: a
       * guest orders a beer from their phone at 20:04, somebody walks over and
       * checks at 20:06, the bar pours at 20:07. Collapsing them would lose the
       * only fact an inspector is interested in.
       */
      type: "selection.age_verified";
      payload: { selectionId: string; verifiedBy: string };
    })
  | (Envelope & {
      type: "selection.progressed";
      payload: { selectionId: string; to: FulfillmentState };
    })
  | (Envelope & {
      type: "selection.recalled";
      payload: { selectionId: string; reason: string };
    })
  | (Envelope & {
      type: "selection.voided";
      payload: { selectionId: string; reason: string; authorizedBy: string };
    })
  | (Envelope & {
      type: "selection.reallocated";
      payload: { selectionId: string; allocations: readonly Allocation[] };
    })
  | (Envelope & {
      type: "allowance.applied";
      payload: {
        allowanceId: string;
        checkId: string;
        kind: AllowanceKind;
        selectionId: string;
        amountMinor: bigint;
        reasonCode: string;
        authorizedBy: string;
      };
    })
  | (Envelope & {
      type: "tender.recorded";
      payload: {
        tenderId: string;
        checkId: string;
        kind: TenderKind;
        amountMinor: bigint;
        tipMinor: bigint;
        roundingMinor: bigint;
      };
    })
  | (Envelope & {
      type: "tender.voided";
      payload: {
        tenderId: string;
        reasonCode: string;
        authorizedBy: string;
      };
    })
  | (Envelope & {
      type: "check.refunded";
      payload: {
        refundId: string;
        checkId: string;
        reasonCode: string;
        authorizedBy: string;
        tenders: readonly { kind: TenderKind; amountMinor: bigint }[];
        leaseId: string;
        seriesKey: string;
        period: string;
        number: number;
        receiptNumber: string;
      };
    });

// --- State -----------------------------------------------------------------

export interface OrderState {
  readonly orderId: string;
  readonly orderType: OrderType;
  readonly consumptionMode: ConsumptionMode;
  readonly status: "OPEN" | "SETTLED" | "ABANDONED";
  /** The check a selection lands on unless it says otherwise. */
  readonly primaryCheckId: string;
  readonly tableRef: string | null;
  /** The number a counter guest is called by; null at a table. */
  readonly pickupCode: string | null;
  /** Guests at the table; null when nobody was asked. */
  readonly covers: number | null;
  readonly reservationRef: string | null;
  readonly selections: ReadonlyMap<string, SelectionState>;
  readonly checks: ReadonlyMap<string, CheckState>;
  readonly allowances: ReadonlyMap<string, AllowanceState>;
  readonly tenders: ReadonlyMap<string, TenderState>;
  readonly anomalies: readonly Anomaly[];
  /** Optimistic concurrency token. Advances on every event that lands. */
  readonly version: number;
  /** Global dedup, so a retried outbox cannot double-charge. */
  readonly appliedEventIds: ReadonlySet<string>;
}

/** Mutable working copy — the fold is pure from the outside. */
interface Draft {
  orderId: string;
  orderType: OrderType;
  consumptionMode: ConsumptionMode;
  status: "OPEN" | "SETTLED" | "ABANDONED";
  primaryCheckId: string;
  tableRef: string | null;
  pickupCode: string | null;
  covers: number | null;
  reservationRef: string | null;
  selections: Map<string, SelectionState>;
  checks: Map<string, CheckState>;
  allowances: Map<string, AllowanceState>;
  tenders: Map<string, TenderState>;
  anomalies: Anomaly[];
  version: number;
  appliedEventIds: Set<string>;
}

const freeze = (d: Draft): OrderState => ({ ...d });

const thaw = (s: OrderState): Draft => ({
  orderId: s.orderId,
  orderType: s.orderType,
  consumptionMode: s.consumptionMode,
  status: s.status,
  primaryCheckId: s.primaryCheckId,
  tableRef: s.tableRef,
  pickupCode: s.pickupCode,
  covers: s.covers,
  reservationRef: s.reservationRef,
  selections: new Map(s.selections),
  checks: new Map(s.checks),
  allowances: new Map(s.allowances),
  tenders: new Map(s.tenders),
  anomalies: [...s.anomalies],
  version: s.version,
  appliedEventIds: new Set(s.appliedEventIds),
});

// --- Fulfillment, derived from stamps rather than arrival order -------------

const rankToState = (rank: number): FulfillmentState => BY_RANK[rank] ?? "NEW";

/**
 * The line's state, as a pure function of everything ever reported about it.
 *
 * A recall resets the line to SENT and invalidates progress reported BEFORE
 * it; progress reported after applies on top. Expressing it this way — rather
 * than mutating a field as events arrive — is what makes the aggregate
 * converge: replay the same observations in any order and the answer is the
 * same.
 */
function deriveFulfillment(log: readonly FulfillmentObservation[]): FulfillmentState {
  let lastRecall: string | undefined;
  for (const o of log) {
    if (o.kind === "RECALL" && (lastRecall === undefined || o.hlc > lastRecall)) lastRecall = o.hlc;
  }

  let rank = lastRecall === undefined ? FULFILLMENT_RANK.NEW : FULFILLMENT_RANK.SENT;
  for (const o of log) {
    if (o.kind !== "PROGRESS") continue;
    if (lastRecall !== undefined && o.hlc < lastRecall) continue;
    if (o.rank > rank) rank = o.rank;
  }
  return rankToState(rank);
}

/** Earliest of two instants, tolerating a null. Commutative, hence convergent. */
const earliest = (a: Date | null, b: Date): Date =>
  a === null ? b : a.getTime() <= b.getTime() ? a : b;

function withObservation(
  selection: SelectionState,
  observation: FulfillmentObservation,
): SelectionState {
  const fulfillmentLog = [...selection.fulfillmentLog, observation];
  return { ...selection, fulfillmentLog, fulfillmentState: deriveFulfillment(fulfillmentLog) };
}

// --- The reducer -----------------------------------------------------------

type Outcome = "APPLIED" | "IGNORED" | "DEFERRED" | "REJECTED";

interface ApplyResult {
  readonly outcome: Outcome;
  readonly anomalies?: readonly Omit<Anomaly, "eventId">[];
}

const applied: ApplyResult = { outcome: "APPLIED" };
const ignored: ApplyResult = { outcome: "IGNORED" };
const defer = (kind: AnomalyKind, detail: string): ApplyResult => ({
  outcome: "DEFERRED",
  anomalies: [{ kind, detail }],
});
const rejected = (kind: AnomalyKind, detail: string): ApplyResult => ({
  outcome: "REJECTED",
  anomalies: [{ kind, detail }],
});

/**
 * Fold one event onto the state, sequentially.
 *
 * This is the server's ingest path, where events arrive in assigned sequence
 * order. An event whose prerequisite is missing is an anomaly here, because
 * there is no "later" — use `foldOrderConverging` for a shuffled or offline
 * batch, where there is.
 */
export function reduceOrder(
  state: OrderState | undefined,
  event: OrderEvent,
): OrderState | undefined {
  const step = stepOrder(state, event);
  return step.state;
}

interface Step {
  readonly state: OrderState | undefined;
  readonly outcome: Outcome;
}

function stepOrder(state: OrderState | undefined, event: OrderEvent, buffering = false): Step {
  // Global dedup first: everything below assumes an event is applied once.
  if (state?.appliedEventIds.has(event.eventId)) return { state, outcome: "IGNORED" };

  if (event.type === "order.opened") {
    if (state) {
      const d = thaw(state);
      record(d, event, [{ kind: "ALREADY_OPEN", detail: `order ${state.orderId} is already open` }]);
      return { state: freeze(d), outcome: "REJECTED" };
    }
    const { orderId, orderType, consumptionMode, checkId, tableRef, pickupCode, covers, reservationRef } = event.payload;
    return {
      outcome: "APPLIED",
      state: freeze({
        orderId,
        orderType,
        consumptionMode,
        status: "OPEN",
        primaryCheckId: checkId,
        tableRef: tableRef ?? null,
        pickupCode: pickupCode ?? null,
        covers: covers ?? null,
        reservationRef: reservationRef ?? null,
        selections: new Map(),
        checks: new Map([[checkId, { checkId, closedAt: null, buyer: null, refund: null }]]),
        allowances: new Map(),
        tenders: new Map(),
        anomalies: [],
        version: 1,
        appliedEventIds: new Set([event.eventId]),
      }),
    };
  }

  // Nothing can happen to an order that has not been opened. Under the
  // converging fold this is a deferral; sequentially it is a dropped event
  // that an upstream ordering buffer will replay.
  if (!state) return { state: undefined, outcome: "DEFERRED" };

  const d = thaw(state);
  const result = apply(d, event);

  if (result.outcome === "DEFERRED" && buffering) {
    // Nothing recorded: the event has not been judged yet.
    return { state, outcome: "DEFERRED" };
  }

  if (result.outcome === "IGNORED") {
    d.appliedEventIds.add(event.eventId);
    return { state: freeze(d), outcome: "IGNORED" };
  }

  // A deferral that cannot be retried is a rejection.
  record(d, event, result.anomalies ?? []);
  d.appliedEventIds.add(event.eventId);
  d.version += 1;
  d.status = deriveStatus(d);
  return {
    state: freeze(d),
    outcome: result.outcome === "DEFERRED" ? "REJECTED" : result.outcome,
  };
}

function record(d: Draft, event: OrderEvent, anomalies: readonly Omit<Anomaly, "eventId">[]): void {
  for (const a of anomalies) d.anomalies.push({ ...a, eventId: event.eventId });
}

/**
 * A check with nothing on it that could owe or be owed: no live line allocated
 * to it and no live tender against it.
 *
 * Merging a split table back moves every line home and leaves the other checks
 * open and EMPTY. Counting those as "not yet closed" kept the order open for
 * ever — the table occupied at 0,00 Kč and a paid receipt read back as unpaid.
 * Closing them instead would mint a zero-value tax document. So an empty check
 * simply does not count; see `order.spec.ts`, "AN EMPTY CHECK DOES NOT HOLD A
 * TABLE".
 */
export function isCheckVacant(
  state: Pick<Readable, "selections" | "tenders">,
  checkId: string,
): boolean {
  for (const s of state.selections.values()) {
    if (s.voided === null && s.allocations.some((a) => a.checkId === checkId)) return false;
  }
  for (const t of state.tenders.values()) {
    if (t.voided === null && t.checkId === checkId) return false;
  }
  return true;
}

// Abandoning is final: it is not something the checks can derive back to OPEN.
// SETTLED needs at least one CLOSED check — vacancy alone is a table nobody
// rang anything on, which leaves through `order.abandoned`, never as "paid".
const deriveStatus = (d: Draft): OrderState["status"] => {
  if (d.status === "ABANDONED") return "ABANDONED";
  const checks = [...d.checks.values()];
  const anyClosed = checks.some((c) => c.closedAt !== null);
  const allDone = checks.every((c) => c.closedAt !== null || isCheckVacant(d, c.checkId));
  return checks.length > 0 && anyClosed && allDone ? "SETTLED" : "OPEN";
};

/** Fold a whole stream in the order given. */
export function foldOrder(events: readonly OrderEvent[]): OrderState | undefined {
  let state: OrderState | undefined;
  for (const event of events) state = stepOrder(state, event).state;
  return state;
}

/**
 * Fold a stream that may have arrived out of order.
 *
 * Events whose prerequisite has not landed yet are held and retried after
 * every round that made progress, rather than rejected on sight. This is the
 * offline path: a handheld's outbox drains in whatever order the network
 * allowed, and a `selection.sent` overtaking its own `selection.added` must not
 * cost the venue a plate of food.
 *
 * Once a round makes no progress the remainder is judged, so a genuinely
 * orphaned event still surfaces as an anomaly rather than vanishing.
 */
export function foldOrderConverging(events: readonly OrderEvent[]): OrderState | undefined {
  let state: OrderState | undefined;
  let pending = [...events];

  for (;;) {
    const deferred: OrderEvent[] = [];
    let progressed = false;

    for (const event of pending) {
      const step = stepOrder(state, event, true);
      if (step.outcome === "DEFERRED") {
        deferred.push(event);
        continue;
      }
      state = step.state;
      progressed = true;
    }

    pending = deferred;
    if (!progressed || pending.length === 0) break;
  }

  // Nothing left to wait for: judge what remains.
  for (const event of pending) state = stepOrder(state, event, false).state;
  return state;
}

/**
 * Types this module owns. Anything else on the stream belongs to somebody else.
 */
const ORDER_TYPES = new Set<string>([
  "order.opened",
  "order.consumption_mode_changed",
  "check.opened",
  "check.closed",
  "check.refunded",
  "check.buyer_identified",
  "selection.added",
  "selection.repriced",
  "selection.requantified",
  "selection.retaxed",
  "order.table_changed",
  "order.abandoned",
  "selection.sent",
  "selection.age_verified",
  "selection.progressed",
  "selection.recalled",
  "selection.voided",
  "selection.reallocated",
  "allowance.applied",
  "tender.recorded",
  "tender.voided",
]);

function apply(d: Draft, event: OrderEvent): ApplyResult {
  /**
   * NOT EVERY EVENT ON THE ORDER STREAM IS AN ORDER EVENT.
   *
   * The kitchen's tickets share this stream on purpose — one order fired to the
   * grill and the bar is two tickets and ONE order, and a ticket-per-stream
   * would make "what happened to table 14" a join instead of a fold. So a
   * replay feeds `ticket.fired` and its siblings through here.
   *
   * The switch below returned `undefined` for them, which reached `stepOrder`
   * as `result.outcome` on nothing and crashed the fold — on the till, at the
   * moment of firing. Ignoring what this module does not own is the correct
   * behaviour for a shared stream, and the alternative is a module that has to
   * know about every other module's events.
   */
  if (!ORDER_TYPES.has(event.type)) return { outcome: "IGNORED" };

  // A settled order is closed to everything except reading it. A reversal
  // after settlement is `check.refunded` — a corrective document with its own
  // number, recorded against the check it corrects, not a mutation of history.
  //
  // Kitchen progress is not a change to the order either: a counter sale is
  // PAID FIRST and cooked after, so its READY arrives on a settled order.
  const mutatesOrder =
    event.type !== "check.closed" &&
    event.type !== "order.consumption_mode_changed" &&
    event.type !== "check.refunded" &&
    event.type !== "selection.progressed" &&
    event.type !== "selection.recalled";
  if (d.status !== "OPEN" && mutatesOrder) {
    return defer("ORDER_NOT_OPEN", `order ${d.orderId} has settled; ${event.type} refused`);
  }

  switch (event.type) {
    case "order.opened":
      return rejected("ALREADY_OPEN", `order ${d.orderId} is already open`);

    case "order.consumption_mode_changed": {
      // Only the mode moves here. Re-resolving VAT on the existing lines is a
      // separate decision made against the rule registry, arriving as
      // selection.retaxed — which keeps this reducer pure.
      d.consumptionMode = event.payload.consumptionMode;
      return applied;
    }

    case "order.table_changed": {
      d.tableRef = event.payload.tableRef;
      return applied;
    }

    case "order.abandoned": {
      // Only an order nothing happened on. A line that was rung — even voided
      // after the kitchen saw it — or money that was taken is a sale's history,
      // and it closes through a check, not by vanishing.
      const live = [...d.selections.values()].filter((s) => s.voided === null);
      if (live.length > 0) {
        return rejected("ORDER_NOT_EMPTY", `order ${d.orderId} has ${live.length} line(s) on it`);
      }
      if ([...d.selections.values()].some((s) => s.firedAt !== null)) {
        return rejected("ORDER_WAS_FIRED", `order ${d.orderId} reached the kitchen; close it instead`);
      }
      if ([...d.tenders.values()].some((t) => !t.voided)) {
        return rejected("ORDER_HAS_TENDERS", `order ${d.orderId} has money on it`);
      }
      d.status = "ABANDONED";
      return applied;
    }

    case "check.opened": {
      if (d.checks.has(event.payload.checkId)) return ignored;
      d.checks.set(event.payload.checkId, {
        checkId: event.payload.checkId,
        closedAt: null,
        buyer: null,
        refund: null,
      });
      return applied;
    }

    case "check.buyer_identified": {
      const check = d.checks.get(event.payload.checkId);
      if (!check) return defer("UNKNOWN_CHECK", `no check ${event.payload.checkId}`);
      /**
       * A SETTLED CHECK CANNOT ACQUIRE A BUYER.
       *
       * The document is printed and in somebody's hand. Naming a buyer
       * afterwards produces a filed record that disagrees with the paper, and
       * the paper is the one the tax authority will be shown. Converting after
       * the fact is a credit note and a new document, not an edit.
       */
      if (check.closedAt !== null) return ignored;

      d.checks.set(event.payload.checkId, {
        ...check,
        buyer: {
          legalName: event.payload.legalName,
          registrationId: event.payload.registrationId ?? null,
          vatId: event.payload.vatId ?? null,
          address: event.payload.address ?? null,
          source: event.payload.source === "ARES" ? "ARES" : "MANUAL",
        },
      });
      return applied;
    }

    /**
     * NOT THE AGGREGATE'S, and said out loud rather than left to the guard.
     *
     * `ORDER_TYPES` already excludes it, so this case is unreachable — but the
     * switch is exhaustive over the union and the compiler asked, which is the
     * right moment to state the position: a receipt number is an identifier the
     * fiscal projection reads off this stream, and it changes NOTHING about
     * what was sold. Folding it into order state would create a second copy of
     * a fact the log already holds.
     */
    case "receipt.issued":
      return ignored;

    case "check.closed": {
      const check = d.checks.get(event.payload.checkId);
      if (!check) return defer("UNKNOWN_CHECK", `no check ${event.payload.checkId}`);
      if (check.closedAt) return ignored;

      const balance = settlementBalanceOf(d, check.checkId);
      if (balance !== 0n) {
        // Deferred rather than rejected: a close overtaking its own tender is
        // ordinary on a reconnecting device. If the tender never arrives, the
        // final pass turns this into the anomaly it deserves to be.
        return defer(
          "CHECK_NOT_BALANCED",
          `check ${check.checkId} is out by ${balance} minor units; tenders plus rounding must ` +
            `equal the amount due, exactly`,
        );
      }

      d.checks.set(check.checkId, { ...check, closedAt: event.occurredAt });
      return applied;
    }

    case "selection.added": {
      const p = event.payload;
      if (d.selections.has(p.selectionId)) {
        return rejected("DUPLICATE_SELECTION", `selection ${p.selectionId} already exists`);
      }
      const checkId = p.checkId ?? d.primaryCheckId;
      if (!d.checks.has(checkId)) return defer("UNKNOWN_CHECK", `no check ${checkId}`);

      d.selections.set(p.selectionId, {
        selectionId: p.selectionId,
        itemRef: p.itemRef,
        name: p.name,
        quantityMilli: p.quantityMilli,
        priced: p.priced,
        allergens: p.allergens ?? [],
        fulfillmentState: "NEW",
        fulfillmentLog: [],
        firedAt: null,
        allocations: [{ checkId, weightBp: 10_000 }],
        voided: null,
        /**
         * `=== true`, so a stream written before this field existed reads as
         * unrestricted.
         *
         * The safe reading of a missing value is "we do not know", and the safe
         * handling of that would be to gate the line — which would freeze every
         * bar in the country on every historical order the moment this shipped.
         * `false` is chosen with the trade stated: no stream in existence
         * predates this by more than days, and every new line carries the flag.
         */
        ageRestricted: p.ageRestricted === true,
        ageVerifiedBy: null,
        ageVerifiedAt: null,
        ...(p.seatNumber !== undefined ? { seatNumber: p.seatNumber } : {}),
        ...(p.courseId !== undefined ? { courseId: p.courseId } : {}),
        modifiers: p.modifiers ?? [],
        note: p.note ?? null,
        addedBy: event.actorId,
      });
      return applied;
    }

    case "selection.age_verified": {
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);

      /**
       * KEEPS THE FIRST, like every other "who did this and when" in the log.
       *
       * Two servers both confirming is ordinary — one walks over, another is
       * already there. The record that matters is who checked BEFORE the drink
       * was poured, and a later confirmation overwriting it would rewrite the
       * answer to the only question anybody will ask.
       */
      if (sel.ageVerifiedBy !== null) return applied;

      d.selections.set(sel.selectionId, {
        ...sel,
        ageVerifiedBy: event.payload.verifiedBy,
        ageVerifiedAt: event.occurredAt,
      });
      return applied;
    }

    case "selection.repriced": {
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);
      if (sel.voided) return rejected("VOIDED_SELECTION", `selection ${sel.selectionId} is voided`);
      if (FULFILLMENT_RANK[sel.fulfillmentState] >= FULFILLMENT_RANK.SENT) {
        return rejected(
          "SENT_SELECTION_IMMUTABLE",
          `selection ${sel.selectionId} has been sent to the kitchen; change it with a void ` +
            `and a new selection so the kitchen and the check can still be reconciled`,
        );
      }
      d.selections.set(sel.selectionId, { ...sel, priced: event.payload.priced });
      return applied;
    }

    case "selection.requantified": {
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);
      if (sel.voided) return rejected("VOIDED_SELECTION", `selection ${sel.selectionId} is voided`);
      if (FULFILLMENT_RANK[sel.fulfillmentState] >= FULFILLMENT_RANK.SENT) {
        return rejected(
          "SENT_SELECTION_IMMUTABLE",
          `selection ${sel.selectionId} has been sent to the kitchen; change it with a void ` +
            `and a new selection so the kitchen and the check can still be reconciled`,
        );
      }
      if (event.payload.quantityMilli <= 0) {
        return rejected("BAD_QUANTITY", "a quantity of zero is a void, and is recorded as one");
      }
      // An allowance was granted against the line as it was; moving the
      // quantity under it would change what the discount means.
      if ([...d.allowances.values()].some((a) => a.selectionId === sel.selectionId)) {
        return rejected("HAS_ALLOWANCE", `selection ${sel.selectionId} carries a discount or comp`);
      }
      d.selections.set(sel.selectionId, {
        ...sel,
        quantityMilli: event.payload.quantityMilli,
        priced: event.payload.priced,
      });
      return applied;
    }

    case "selection.retaxed": {
      // Allowed on a SENT line: the guest pays the same, but the law that
      // applies has changed (dine-in became takeaway). The gross is untouched.
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);
      d.selections.set(sel.selectionId, {
        ...sel,
        priced: {
          grossMinor: sel.priced.grossMinor,
          vatMinor: event.payload.vatMinor,
          netMinor: event.payload.netMinor,
          rateBp: event.payload.rateBp,
          taxSourceRef: event.payload.taxSourceRef,
        },
      });
      return applied;
    }

    case "selection.sent": {
      const missing = event.payload.selectionIds.filter((id) => !d.selections.has(id));
      if (missing.length > 0) {
        // All or nothing, so a partially-applied fire cannot depend on which
        // half of the batch landed first.
        return defer("UNKNOWN_SELECTION", `no selection ${missing.join(", ")}`);
      }
      for (const selectionId of event.payload.selectionIds) {
        const sel = d.selections.get(selectionId)!;
        d.selections.set(
          selectionId,
          withObservation(
            // The earliest fire wins, so two devices reporting the same send
            // cannot move the anchor the theft report depends on.
            { ...sel, firedAt: earliest(sel.firedAt, event.occurredAt) },
            {
              kind: "PROGRESS",
              rank: FULFILLMENT_RANK.SENT,
              hlc: event.hlc,
              eventId: event.eventId,
            },
          ),
        );
      }
      return applied;
    }

    case "selection.progressed": {
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);

      const before = sel.fulfillmentState;
      const next = withObservation(sel, {
        kind: "PROGRESS",
        rank: FULFILLMENT_RANK[event.payload.to],
        hlc: event.hlc,
        eventId: event.eventId,
      });
      d.selections.set(sel.selectionId, next);

      if (FULFILLMENT_RANK[event.payload.to] <= FULFILLMENT_RANK[before]) {
        // Ordinary on reconnection: a KDS whose outbox drained slowly reports
        // IN_PROGRESS after READY already landed. The observation is kept and
        // the derived state is unmoved; the flag exists because a lagging
        // station is worth knowing about.
        return {
          outcome: "APPLIED",
          anomalies: [
            {
              kind: "NON_MONOTONIC_FULFILLMENT",
              detail:
                `selection ${sel.selectionId} was already ${before}; the report of ` +
                `${event.payload.to} does not move it. Use selection.recalled to reverse ` +
                `deliberately.`,
            },
          ],
        };
      }
      return applied;
    }

    case "selection.recalled": {
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);
      // The one legitimate way backwards, and it is explicit. firedAt is
      // deliberately untouched: clearing it would let recalls flatter
      // speed-of-service reporting.
      d.selections.set(
        sel.selectionId,
        withObservation(sel, {
          kind: "RECALL",
          rank: FULFILLMENT_RANK.SENT,
          hlc: event.hlc,
          eventId: event.eventId,
        }),
      );
      return applied;
    }

    case "selection.voided": {
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);
      if (sel.voided) return ignored;
      d.selections.set(sel.selectionId, {
        ...sel,
        // firedAt survives. This is the fired-but-not-billed anchor.
        voided: {
          at: event.occurredAt,
          reason: event.payload.reason,
          authorizedBy: event.payload.authorizedBy,
        },
      });
      return applied;
    }

    case "selection.reallocated": {
      const sel = d.selections.get(event.payload.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${event.payload.selectionId}`);

      const allocations = event.payload.allocations;
      const total = allocations.reduce((acc, a) => acc + a.weightBp, 0);
      if (total !== 10_000) {
        // Normalising would quietly move money between guests. Refuse.
        return rejected(
          "ALLOCATION_NOT_WHOLE",
          `allocations for ${sel.selectionId} sum to ${total} bp, not 10000`,
        );
      }
      const unknown = allocations.find((a) => !d.checks.has(a.checkId));
      if (unknown) return defer("UNKNOWN_CHECK", `no check ${unknown.checkId}`);

      d.selections.set(sel.selectionId, { ...sel, allocations: [...allocations] });
      return applied;
    }

    case "allowance.applied": {
      const p = event.payload;
      if (d.allowances.has(p.allowanceId)) return ignored;
      if (!d.checks.has(p.checkId)) return defer("UNKNOWN_CHECK", `no check ${p.checkId}`);

      const sel = d.selections.get(p.selectionId);
      if (!sel) return defer("UNKNOWN_SELECTION", `no selection ${p.selectionId}`);

      if (!p.reasonCode.trim()) {
        // A reason code is what makes discount abuse detectable at all, and it
        // is the difference between a manager's judgement and a leak.
        return rejected("ALLOWANCE_WITHOUT_REASON", `allowance ${p.allowanceId} has no reason code`);
      }
      if (p.amountMinor > sel.priced.grossMinor) {
        return rejected(
          "ALLOWANCE_EXCEEDS_LINE",
          `allowance ${p.amountMinor} exceeds the ${sel.priced.grossMinor} line it targets`,
        );
      }

      d.allowances.set(p.allowanceId, {
        allowanceId: p.allowanceId,
        checkId: p.checkId,
        kind: p.kind,
        selectionId: p.selectionId,
        amountMinor: p.amountMinor,
        reasonCode: p.reasonCode,
        authorizedBy: p.authorizedBy,
      });
      return applied;
    }

    case "tender.recorded": {
      const p = event.payload;
      if (d.tenders.has(p.tenderId)) return ignored;
      if (!d.checks.has(p.checkId)) return defer("UNKNOWN_CHECK", `no check ${p.checkId}`);
      d.tenders.set(p.tenderId, {
        tenderId: p.tenderId,
        checkId: p.checkId,
        kind: p.kind,
        amountMinor: p.amountMinor,
        tipMinor: p.tipMinor,
        roundingMinor: p.roundingMinor,
        voided: null,
      });
      return applied;
    }

    case "check.refunded": {
      const p = event.payload;
      const check = d.checks.get(p.checkId);
      if (!check) return defer("UNKNOWN_CHECK", `no check ${p.checkId}`);
      if (check.refund?.refundId === p.refundId) return ignored;
      // Deferred: on a reconnecting device the refund can overtake its close.
      // An open check is taken back line by line — that is a void, not this.
      if (!check.closedAt) return defer("CHECK_NOT_CLOSED", `check ${p.checkId} has not closed`);
      if (check.refund) {
        return rejected("ALREADY_REFUNDED", `check ${p.checkId} was taken back by ${check.refund.refundId}`);
      }
      if (!p.reasonCode.trim()) {
        return rejected("REFUND_WITHOUT_REASON", `refund ${p.refundId} has no reason code`);
      }

      // Exactly what was paid, kind by kind: a card is refunded to the card and
      // a cash sale in cash, or the drawer and the acquirer both stop agreeing.
      const paid = new Map<string, bigint>();
      for (const t of d.tenders.values()) {
        if (t.checkId !== p.checkId || t.voided) continue;
        paid.set(t.kind, (paid.get(t.kind) ?? 0n) + t.amountMinor);
      }
      const back = new Map<string, bigint>();
      for (const t of p.tenders) back.set(t.kind, (back.get(t.kind) ?? 0n) + t.amountMinor);
      const mirrors =
        paid.size === back.size && [...paid].every(([kind, amount]) => back.get(kind) === amount);
      if (!mirrors) {
        return rejected(
          "REFUND_NOT_WHOLE",
          `refund ${p.refundId} gives back ${[...back].map(([k, a]) => `${k} ${a}`).join(", ")}; ` +
            `the check was paid ${[...paid].map(([k, a]) => `${k} ${a}`).join(", ")}`,
        );
      }

      d.checks.set(p.checkId, {
        ...check,
        refund: {
          refundId: p.refundId,
          at: event.occurredAt,
          reasonCode: p.reasonCode,
          authorizedBy: p.authorizedBy,
          tenders: p.tenders.map((t) => ({ kind: t.kind, amountMinor: t.amountMinor })),
          receiptNumber: p.receiptNumber,
        },
      });
      return applied;
    }

    case "tender.voided": {
      const p = event.payload;
      const tender = d.tenders.get(p.tenderId);
      // Deferred, not rejected: on a reconnecting device the reversal can
      // arrive ahead of the tender it reverses.
      if (!tender) return defer("UNKNOWN_TENDER", `no tender ${p.tenderId}`);
      if (tender.voided) return ignored;
      if (!p.reasonCode.trim()) {
        // Same rule as a line void, for the same reason: an unexplained
        // reversal of money taken is the single highest-signal fraud pattern
        // there is, and it is only a signal if the reason is mandatory.
        return rejected(
          "TENDER_VOID_WITHOUT_REASON",
          `tender ${p.tenderId} cannot be reversed without a reason code`,
        );
      }
      d.tenders.set(p.tenderId, {
        ...tender,
        voided: { reasonCode: p.reasonCode, authorizedBy: p.authorizedBy },
      });
      return applied;
    }
  }
}

// --- Derived totals --------------------------------------------------------

export interface CheckTaxLine {
  readonly rateBp: RateBp;
  readonly grossMinor: bigint;
  readonly vatMinor: bigint;
  readonly netMinor: bigint;
}

export interface CheckTotals {
  /** What was rung, before allowances. Preserved for food cost and analytics. */
  readonly grossMinor: bigint;
  readonly discountMinor: bigint;
  readonly compMinor: bigint;
  /** What the guest owes. */
  readonly dueMinor: bigint;
  readonly vatMinor: bigint;
  readonly netMinor: bigint;
  /** Tips are on top of the check and are a liability, never revenue. */
  readonly tipMinor: bigint;
  readonly roundingMinor: bigint;
  readonly taxLines: readonly CheckTaxLine[];
}

interface Readable {
  readonly selections: ReadonlyMap<string, SelectionState>;
  readonly allowances: ReadonlyMap<string, AllowanceState>;
  readonly tenders: ReadonlyMap<string, TenderState>;
}

/**
 * A selection's gross split across the checks it is allocated to.
 *
 * Computed for ALL checks at once and then indexed, so the split is identical
 * no matter which check asked. Largest remainder, so the parts sum to the whole
 * exactly: three guests splitting a 189 CZK guláš must not create or destroy a
 * haléř between them.
 */
function shareOf(selection: SelectionState, checkId: string): bigint {
  if (selection.voided) return 0n;
  if (selection.allocations.length === 1) {
    return selection.allocations[0]!.checkId === checkId ? selection.priced.grossMinor : 0n;
  }
  const shares = allocateProportionally(
    selection.priced.grossMinor,
    selection.allocations.map((a) => ({ key: a.checkId, weight: BigInt(a.weightBp) })),
  );
  return shares[checkId] ?? 0n;
}

export function checkTotals(state: Readable, checkId: string): CheckTotals {
  let grossMinor = 0n;
  // Chargeable gross per rate, after allowances. VAT is computed on the
  // per-rate SUM rather than per line: §37 ZDPH applies the rate to the base,
  // and summing per-line VAT drifts by a haléř or two over a long check.
  const chargeableByRate = new Map<RateBp, bigint>();

  let discountMinor = 0n;
  let compMinor = 0n;

  const allowancesFor = (selectionId: string): { discount: bigint; comp: bigint } => {
    let discount = 0n;
    let comp = 0n;
    for (const a of state.allowances.values()) {
      if (a.checkId !== checkId || a.selectionId !== selectionId) continue;
      if (a.kind === "DISCOUNT") discount += a.amountMinor;
      else comp += a.amountMinor;
    }
    return { discount, comp };
  };

  for (const selection of state.selections.values()) {
    const share = shareOf(selection, checkId);
    if (share === 0n) continue;
    grossMinor += share;

    const { discount, comp } = allowancesFor(selection.selectionId);
    discountMinor += discount;
    compMinor += comp;

    // Both kinds reduce what is owed. Only a discount reduces the taxable
    // base; a comp has no consideration at all, so it leaves the base too —
    // the difference between them lives in which series they land in, and in
    // the fact that the gross above still records that the food was made.
    const chargeable = share - discount - comp;
    const rate = selection.priced.rateBp;
    chargeableByRate.set(rate, (chargeableByRate.get(rate) ?? 0n) + chargeable);
  }

  const taxLines: CheckTaxLine[] = [...chargeableByRate.entries()]
    .filter(([, gross]) => gross !== 0n)
    .sort((a, b) => a[0] - b[0])
    .map(([rateBp, gross]) => {
      const vat = vatFromGross(gross, rateBp);
      return { rateBp, grossMinor: gross, vatMinor: vat, netMinor: gross - vat };
    });

  const dueMinor = taxLines.reduce((acc, l) => acc + l.grossMinor, 0n);
  const vatMinor = taxLines.reduce((acc, l) => acc + l.vatMinor, 0n);

  let tipMinor = 0n;
  let roundingMinor = 0n;
  for (const t of state.tenders.values()) {
    // Voided tenders leave the totals too, not only the balance. A reversed
    // cash tender that kept its rounding here would put 50 haléře into the
    // drawer expectation that nobody ever put into the drawer.
    if (t.checkId !== checkId || t.voided) continue;
    tipMinor += t.tipMinor;
    roundingMinor += t.roundingMinor;
  }

  return {
    grossMinor,
    discountMinor,
    compMinor,
    dueMinor,
    vatMinor,
    netMinor: dueMinor - vatMinor,
    tipMinor,
    roundingMinor,
    taxLines,
  };
}

export interface CheckLine {
  readonly selectionId: string;
  readonly itemRef: string;
  readonly name: string;
  /** This check's part of the quantity, when a line was split across checks. */
  readonly quantityMilli: number;
  /** This check's share of the line's price. Zero for a voided line. */
  readonly grossMinor: bigint;
  readonly discountMinor: bigint;
  readonly compMinor: bigint;
  /** What the guest was charged for it: share less discount and comp. */
  readonly chargedMinor: bigint;
  readonly rateBp: RateBp;
  readonly voided: boolean;
  /** What a voided line would have cost — void reports need it. */
  readonly voidedGrossMinor: bigint;
}

/**
 * What each line of a check sold, by the same arithmetic as `checkTotals`, so
 * an item report and the day's takings agree to the haléř: the charged amounts
 * sum to the check's due.
 */
export function checkLines(state: Readable, checkId: string): CheckLine[] {
  const out: CheckLine[] = [];
  for (const selection of state.selections.values()) {
    const onThisCheck = selection.allocations.some((a) => a.checkId === checkId);
    if (!onThisCheck) continue;
    let discount = 0n;
    let comp = 0n;
    for (const a of state.allowances.values()) {
      if (a.checkId !== checkId || a.selectionId !== selection.selectionId) continue;
      if (a.kind === "DISCOUNT") discount += a.amountMinor;
      else comp += a.amountMinor;
    }
    const share = shareOf(selection, checkId);
    const whole = selection.priced.grossMinor;
    const voided = selection.voided !== null;
    const portion = voided || whole === 0n ? 1 : Number(share) / Number(whole);
    out.push({
      selectionId: selection.selectionId,
      itemRef: selection.itemRef,
      name: selection.name,
      quantityMilli: Math.round(selection.quantityMilli * portion),
      grossMinor: share,
      discountMinor: voided ? 0n : discount,
      compMinor: voided ? 0n : comp,
      chargedMinor: voided ? 0n : share - discount - comp,
      rateBp: selection.priced.rateBp,
      voided,
      voidedGrossMinor: voided ? whole : 0n,
    });
  }
  return out;
}

function settlementBalanceOf(state: Readable, checkId: string): bigint {
  const due = checkTotals(state, checkId).dueMinor;
  let tendered = 0n;
  let rounding = 0n;
  for (const t of state.tenders.values()) {
    if (t.checkId !== checkId || t.voided) continue;
    tendered += t.amountMinor;
    // Amount and rounding leave together. Dropping one without the other is
    // how a reversed cash tender leaves a phantom 50 haléře on the check.
    rounding += t.roundingMinor;
  }
  // Σ amount == due + Σ rounding. See the sign convention on TenderState.
  return due + rounding - tendered;
}

/**
 * What is still owed on a check, in minor units. Zero, exactly, or it does not
 * close.
 *
 * `Σ tenders + rounding == due` is an equality and not a tolerance. A cent of
 * slack here becomes an unexplainable drawer variance and, worse, a plausible
 * hiding place.
 */
export function settlementBalance(state: OrderState, checkId: string): bigint {
  return settlementBalanceOf(state, checkId);
}

// --- Fired but not billed --------------------------------------------------

/**
 * Why food that was made is not on a settled check.
 *
 * Every one of these has an innocent explanation. Naming them separately is
 * what lets a manager triage in seconds rather than treating the whole report
 * as noise.
 */
export type FiredNotBilledReason =
  | "VOIDED_AFTER_FIRING"
  | "COMPED_AFTER_FIRING"
  | "CHECK_NEVER_CLOSED";

export interface FiredNotBilledLine {
  readonly orderId: string;
  readonly tableRef: string | null;
  readonly selectionId: string;
  readonly itemRef: string;
  readonly name: string;
  readonly grossMinor: bigint;
  readonly firedAt: Date;
  readonly reason: FiredNotBilledReason;
  /** Elapsed fire-to-removal. A mis-ring is seconds; a meal is many minutes. */
  readonly secondsFiredToRemoval: number | null;
  readonly removalReason: string | null;
  readonly reasonCode: string | null;
  readonly authorizedBy: string | null;
}

/**
 * Food the kitchen made that no closed check charged for.
 *
 * The strongest single rule in the fraud set, and the reason this aggregate is
 * append-only. Most POS cannot compute it — not from oversight but from shape:
 * they overwrite the check rather than appending to it, so once a line is
 * removed there is no record it was ever fired. If a client can PUT a whole
 * check object, this report is impossible and cannot be retrofitted.
 *
 * Deliberately not an accusation. It returns the elapsed time and the
 * authorisation and stops there — no score, no verdict. Automated evaluation
 * of employee behaviour needs human review to be lawful, and a report that
 * concludes for the manager is one they will eventually stop opening.
 */
export function firedNotBilled(state: OrderState): FiredNotBilledLine[] {
  const out: FiredNotBilledLine[] = [];

  const closedChecks = new Set(
    [...state.checks.values()].filter((c) => c.closedAt !== null).map((c) => c.checkId),
  );

  for (const selection of state.selections.values()) {
    // Never fired means the kitchen never saw it. A mis-ring corrected at the
    // till is not a missing plate, and reporting it would bury the real signal.
    if (selection.firedAt === null) continue;

    const comps = [...state.allowances.values()].filter(
      (a) => a.selectionId === selection.selectionId && a.kind === "COMP",
    );
    const compedTotal = comps.reduce((acc, a) => acc + a.amountMinor, 0n);

    let reason: FiredNotBilledReason | undefined;
    let removalAt: Date | null = null;
    let removalReason: string | null = null;
    let reasonCode: string | null = null;
    let authorizedBy: string | null = null;

    if (selection.voided) {
      reason = "VOIDED_AFTER_FIRING";
      removalAt = selection.voided.at;
      removalReason = selection.voided.reason;
      authorizedBy = selection.voided.authorizedBy;
    } else if (compedTotal >= selection.priced.grossMinor) {
      // A PARTIAL discount is not a disappearance: the food was billed, just
      // for less. That is a discount pattern, assessed against peers
      // elsewhere.
      reason = "COMPED_AFTER_FIRING";
      reasonCode = comps[0]?.reasonCode ?? null;
      authorizedBy = comps[0]?.authorizedBy ?? null;
    } else if (!selection.allocations.some((a) => closedChecks.has(a.checkId))) {
      reason = "CHECK_NEVER_CLOSED";
    }

    if (!reason) continue;

    out.push({
      orderId: state.orderId,
      tableRef: state.tableRef,
      selectionId: selection.selectionId,
      itemRef: selection.itemRef,
      name: selection.name,
      grossMinor: selection.priced.grossMinor,
      firedAt: selection.firedAt,
      reason,
      secondsFiredToRemoval:
        removalAt === null
          ? null
          : Math.round((removalAt.getTime() - selection.firedAt.getTime()) / 1000),
      removalReason,
      reasonCode,
      authorizedBy,
    });
  }

  // Oldest fire first: the longest a plate has been unaccounted for is the
  // one worth asking about while anyone still remembers the table.
  return out.sort((a, b) => a.firedAt.getTime() - b.firedAt.getTime());
}
